import React, { useState } from 'react';
import { Card, Typography, Rating, TextField, Button, Box, Snackbar, Alert } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import cdcarelogo from './cdcarelogo.png';
import submitCSRRating, { submitDriverRating } from './firebase';

const theme = createTheme({
  palette: {
    primary: {
      main: '#FF6F00',
    },
  },
});

const DeliveryRatingMessage = ({ itemName, driverName }) => (
  <p>
    Hello, I’m <strong>{driverName}</strong>.<br />
    Please rate my delivery of your <strong>{itemName}</strong>.
  </p>
);

export default function Page({ agentId = '', customerId = '', driverName = '', productName = '', buyId = '' }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
    severity: 'warning',
  });

  const isDriverRating = Boolean(driverName && driverName.trim().length > 0);

  const handleCloseSnackbar = () => setSnackbarState((prev) => ({ ...prev, open: false }));

  const handleRatingChange = (event, newValue) => {
    setRating(newValue);
  };

  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const handleSubmit = async () => {
    const trimmedComment = comment.trim();
    const requiresExplanation = rating <= 1;

    if (rating === 0) {
      setSnackbarState({
        open: true,
        message: 'Please select a rating before submitting.',
        severity: 'warning',
      });
      return;
    }

    if (requiresExplanation && trimmedComment.length < 5) {
      setSnackbarState({
        open: true,
        message: 'Please tell us what went wrong (a short note helps).',
        severity: 'warning',
      });
      return;
    }

    try {
      setLoading(true);
      if (isDriverRating) {
        await submitDriverRating({
          driverId: agentId,
          customerId,
          buyId,
          rating,
          comment: trimmedComment,
        });
      } else {
        await submitCSRRating({
          agentId,
          customerId,
          rating,
          comment: trimmedComment,
        });
      }
      setSubmitted(true);
      setSnackbarState({
        open: true,
        message: 'Thank you for your feedback!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Failed to submit rating', error);
      setSnackbarState({
        open: true,
        message: 'Submission failed. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ backgroundColor: '#f4f4f4', padding: 2 }}
      >
        <Card
          sx={{
            width: '100%',
            maxWidth: 400,
            p: 4,
            borderRadius: 4,
            boxShadow: 3,
            backgroundColor: '#fff',
            textAlign: 'center',
            animation: 'fadeIn 0.4s ease-in-out',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'scale(0.95)' },
              to: { opacity: 1, transform: 'scale(1)' },
            },
          }}
        >
          <img src={cdcarelogo} alt="CDcare Logo" style={{ width: '100px', marginBottom: 20 }} />
          <Typography variant="h6" mb={3}>
            {submitted
              ? '🎉 Thank you for your feedback!'
              : isDriverRating
              ? <DeliveryRatingMessage itemName={productName} driverName={driverName} />
              : 'Kindly rate my service to you.'}
          </Typography>

          {!submitted && (
            <>
              <Box display="flex" justifyContent="center" mb={2}>
                <Rating
                  name="simple-controlled"
                  value={rating}
                  onChange={handleRatingChange}
                  size="large"
                  sx={{ fontSize: 50 }}
                />
              </Box>
              <TextField
                label="Comment"
                multiline
                rows={4}
                placeholder="Share what stood out or how we can improve..."
                variant="outlined"
                fullWidth
                value={comment}
                onChange={handleCommentChange}
                sx={{ mb: 3 }}
              />
              <Button
                variant="contained"
                fullWidth
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  backgroundColor: '#FF6F00',
                  '&:hover': {
                    backgroundColor: '#e65c00',
                  },
                  py: 1.5,
                }}
              >
                {loading ? 'Submitting...' : 'SUBMIT'}
              </Button>
            </>
          )}
        </Card>

        {/* Snackbar for zero rating */}
        <Snackbar
          open={snackbarState.open}
          autoHideDuration={9000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbarState.severity} variant="filled">
            {snackbarState.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}
